import AddIcon from "@material-ui/icons/Add";
import PropTypes from "prop-types";
import React, { Fragment, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { FoldersApi } from "../../../api/folders/folders";
import CustomButton from "../../../components/CustomButton/CustomButton";
import { NO_ITEMS_RESP_ERR } from "../../../data/StringConstants";
import PageSectionHeader from "../../../layouts/Main/components/Page/PageSectionHeader";
import { getQueryParamsFromCurrentHash } from "../../../utils/routingHelpers";
import ItemsContent from "../Items/ItemsContent";

import analytics from "../../../utils/analytics";

/**
 * This container is responsible for data fetching and rendering of corresponding sub-components
 * for the 'All Items' view, which lists all active "Items" available to an organization.
 * 
 * NOTE: An "Item" is just a DBTemplate record whose "TypeDiscriminator" is 1. To be considered
 * active and shown in this result set, the item itself must be available as well as it's corresponding Promotion.
 */
export default function AllItemsContainer({ name, description, user }) {
    let isCancelled = false;
    const history = useHistory();
    const queryParams = getQueryParamsFromCurrentHash(history.location.hash);
    const { sortBy, creator, format, starred } = queryParams;
    const [items, setItems] = useState([]);
    const [totalItemCount, setTotalItemCount] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [hasMore, setHasMore] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);

    // Load the initial set of data once
	useEffect(() => {

		initialFetch();

		return () => (isCancelled = true);
    }, [history.location.hash]);

	/** 
	 * Fetches data for three scenarios:
	 * 1. initial page load
	 * 2. an item is deleted
	 * 3. an item is copied to the same folder
	 * In each of these cases, we need to reset the current page number and items. 
	 * Therefore, we call fetchData with pageNumber = 1 and currentItems = [] by default.
	 * When we do not want to reset the state, we pass in the current page and items.
	 * @param {int} pageNumber: default: 1, which is the first page 
	 * @param {[object]} currentItems: default: [], representing an empty result set 
	 */
	const initialFetch = async (pageNumber = 1, currentItems = []) => {
		fetchData(pageNumber, currentItems);
	};
    
    const fetchData = async (page, items) => {
        setIsLoading(true);
        setCurrentPage(page);

        try {
            const response = await FoldersApi.fetchActiveItemsForOrg({
                sort: sortBy,
                page: page,
                size: 25,
                creatorId: creator,
                formatId: format,
                starred: starred
            });
            if (response.status === 200 && !isCancelled) {
                setTotalItemCount(response.data.TotalItemCount);
                setItems(items.concat(response.data.Data));
                setHasMore(!response.data.IsLastPage);
            }
        } catch (error) {
            console.log(NO_ITEMS_RESP_ERR, error);
        } finally {
            !isCancelled && setIsLoading(false);
        }
    };

    const onCreateItemClicked = () => {
        //no properties passed here, since this event takes a folder id and folder name, which are not present in the All Items view
        analytics.logEvent(analytics.eventTypes.FOLDERS.CREATE_ITEM_CLICKED);
    };

    /**
	 * This is the callback invoked when the infinite scrolling Waypoint is triggered. 
	 */
    const fetchMore = async () => {
		if (hasMore) {
			const updatedPage = currentPage + 1;

			fetchData(updatedPage, items);
		}
	};

    const PrimaryActions = () => (
        user.isEditor && (
            <CustomButton
                startIcon={<AddIcon />}
                color="success"
                variant="contained"
                onClick={onCreateItemClicked}
                href={`#!/search`}
            >
                Create Item
            </CustomButton>
        )
    );

    return (
        <Fragment>
            <PageSectionHeader
                name={name}
                description={description}
                primaryActions={() => <PrimaryActions />}
            />
            <ItemsContent
                queryParams={queryParams}
                items={items}
                copyItem={initialFetch}
                totalItemCount={totalItemCount}
                deleteItem={initialFetch}
                history={history}
                fetchMore={fetchMore}
                isLoading={isLoading}
            />
        </Fragment>
    );
}

AllItemsContainer.propTypes = {
    name: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    user: PropTypes.object.isRequired
}